import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { OrbisLogo } from '@/components/OrbisLogo';
import { Briefcase, Rocket, User, ShoppingCart } from 'lucide-react';

const USE_CASES = [
  { id: 'agency',     icon: Briefcase,    title: 'Agency',     desc: 'Client websites & campaigns' },
  { id: 'saas',       icon: Rocket,       title: 'SaaS',       desc: 'Landing & marketing pages' },
  { id: 'personal',   icon: User,         title: 'Personal',   desc: 'Portfolio & personal brand' },
  { id: 'ecommerce',  icon: ShoppingCart, title: 'eCommerce',  desc: 'Product & store sites' },
];

const FEATURES = [
  { title: 'Magic Mode',     desc: 'Describe it — AI builds the full site' },
  { title: 'Clone Mode',     desc: 'Clone any website in seconds' },
  { title: 'Visual Editor',  desc: 'Edit live without touching code' },
];

const StarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M6.5 1.5L8 5.5H12L8.5 8L10 12L6.5 9.5L3 12L4.5 8L1 5.5H5L6.5 1.5Z" fill="var(--orbis-green)" opacity="0.9"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 8h10M9 4l4 4-4 4" stroke="#08090E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [animDir, setAnimDir] = useState('in'); // 'in' | 'out'

  useEffect(() => {
    if (user && localStorage.getItem(`orbis_onboarding_done_${user.id}`)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const goTo = (next) => {
    setAnimDir('out');
    setTimeout(() => {
      setStep(next);
      setAnimDir('in');
    }, 280);
  };

  const finish = () => {
    if (user) localStorage.setItem(`orbis_onboarding_done_${user.id}`, '1');
    if (selectedUseCase) localStorage.setItem('orbis_use_case', selectedUseCase);
    navigate('/dashboard');
  };

  const skip = () => {
    if (user) localStorage.setItem(`orbis_onboarding_done_${user.id}`, '1');
    navigate('/dashboard');
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  const stepStyle = {
    opacity: animDir === 'out' ? 0 : 1,
    transform: animDir === 'out' ? 'translateY(12px)' : 'translateY(0)',
    transition: 'opacity 0.28s cubic-bezier(0.16,1,0.3,1), transform 0.28s cubic-bezier(0.16,1,0.3,1)',
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', background: '#08090E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

      {/* Full-screen Spline background — public embed URL */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <iframe
          src="https://my.spline.design/etherealringsoflight-dR2pYqCM9bdkEGUV6PlsQVNl/"
          frameBorder="0"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          title="Orbis background"
          loading="lazy"
        />
        {/* Cover the "Made by Spline" badge in the bottom-right corner */}
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 180, height: 48, background: '#08090E', zIndex: 10 }} />
      </div>

      {/* Vignettes */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', background: 'radial-gradient(ellipse 68% 62% at 50% 50%, transparent 0%, rgba(8,9,14,0.10) 38%, rgba(8,9,14,0.52) 68%, rgba(8,9,14,0.86) 100%)' }} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(8,9,14,0.55) 0%, transparent 16%, transparent 82%, rgba(8,9,14,0.55) 100%)' }} />

      {/* Logo */}
      <div style={{ position: 'fixed', top: 36, left: 40, zIndex: 20 }}>
        <OrbisLogo size={22} variant="full" />
      </div>

      {/* Step dots */}
      <div style={{ position: 'fixed', bottom: 44, left: '50%', transform: 'translateX(-50%)', zIndex: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            onClick={() => i < step ? goTo(i) : undefined}
            style={{
              height: 6, borderRadius: 3,
              width: i === step ? 22 : 6,
              background: i === step ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.18)',
              transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)',
              cursor: i < step ? 'pointer' : 'default',
            }}
          />
        ))}
      </div>

      {/* Skip */}
      {step > 0 && (
        <button
          onClick={skip}
          style={{ position: 'fixed', bottom: 48, right: 40, zIndex: 20, background: 'none', border: 'none', fontSize: 13, fontWeight: 300, color: 'var(--orbis-t4)', cursor: 'pointer', fontFamily: 'var(--orbis-font)', letterSpacing: '0.01em', padding: 0 }}
        >
          Skip setup
        </button>
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 640, width: '100%', padding: '0 24px', ...stepStyle }}>

        {/* ── Step 0: Welcome ── */}
        {step === 0 && (
          <>
            <h1 style={{ fontFamily: 'var(--orbis-font-display, Sora, sans-serif)', fontSize: 'clamp(38px, 5vw, 54px)', fontWeight: 200, color: 'var(--orbis-warm)', letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 14 }}>
              Welcome to Orbis,<br />
              <em style={{ fontStyle: 'normal', color: 'var(--orbis-green)' }}>{firstName}.</em>
            </h1>
            <p style={{ fontSize: 15, fontWeight: 300, color: 'var(--orbis-t3)', letterSpacing: '0.01em', lineHeight: 1.65, marginBottom: 48, maxWidth: 360 }}>
              Your AI web studio is ready. Let's get you set up in just a moment.
            </p>
            <PillButton label="Get started" onClick={() => goTo(1)} />
          </>
        )}

        {/* ── Step 1: Use case ── */}
        {step === 1 && (
          <>
            <h1 style={{ fontFamily: 'var(--orbis-font-display, Sora, sans-serif)', fontSize: 'clamp(36px, 4.8vw, 52px)', fontWeight: 200, color: 'var(--orbis-warm)', letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 14 }}>
              What will you<br />
              <em style={{ fontStyle: 'normal', color: 'var(--orbis-green)' }}>create?</em>
            </h1>
            <p style={{ fontSize: 15, fontWeight: 300, color: 'var(--orbis-t3)', marginBottom: 32, maxWidth: 320 }}>
              Help us tailor your experience.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, width: '100%', maxWidth: 480, marginBottom: 36 }}>
              {USE_CASES.map(({ id, icon: Icon, title, desc }) => (
                <button
                  key={id}
                  onClick={() => setSelectedUseCase(id)}
                  style={{
                    background: selectedUseCase === id ? 'rgba(143,196,160,0.08)' : 'rgba(255,255,255,0.04)',
                    border: `0.5px solid ${selectedUseCase === id ? 'rgba(143,196,160,0.45)' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 14, padding: '20px 18px', cursor: 'pointer', textAlign: 'left',
                    display: 'flex', flexDirection: 'column', gap: 8,
                    transition: 'background 0.2s, border-color 0.2s',
                  }}
                >
                  <Icon size={18} style={{ color: selectedUseCase === id ? 'var(--orbis-green)' : 'var(--orbis-t3)', strokeWidth: 1.5 }} />
                  <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--orbis-t1)', letterSpacing: '-0.01em', fontFamily: 'var(--orbis-font)' }}>{title}</div>
                  <div style={{ fontSize: 12, fontWeight: 300, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>{desc}</div>
                </button>
              ))}
            </div>
            <PillButton label="Continue" onClick={() => goTo(2)} />
          </>
        )}

        {/* ── Step 2: Ready ── */}
        {step === 2 && (
          <>
            <h1 style={{ fontFamily: 'var(--orbis-font-display, Sora, sans-serif)', fontSize: 'clamp(36px, 4.8vw, 52px)', fontWeight: 200, color: 'var(--orbis-warm)', letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 14 }}>
              You're<br />
              <em style={{ fontStyle: 'normal', color: 'var(--orbis-green)' }}>all set.</em>
            </h1>
            <p style={{ fontSize: 15, fontWeight: 300, color: 'var(--orbis-t3)', marginBottom: 36, maxWidth: 320 }}>
              Here's what's waiting for you inside.
            </p>

            <div style={{ display: 'flex', flexDirection: 'row', gap: 12, width: '100%', maxWidth: 560, marginBottom: 44 }}>
              {FEATURES.map(({ title, desc }, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                    gap: 10, padding: '20px 16px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '0.5px solid rgba(255,255,255,0.07)',
                    borderRadius: 14,
                    animation: `featurePop 0.4s ${0.1 + i * 0.12}s cubic-bezier(0.16,1,0.3,1) both`,
                  }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(143,196,160,0.12)', border: '0.5px solid rgba(143,196,160,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <StarIcon />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--orbis-t1)', letterSpacing: '-0.01em', marginBottom: 3, fontFamily: 'var(--orbis-font)' }}>{title}</div>
                    <div style={{ fontSize: 11, fontWeight: 300, color: 'var(--orbis-t4)', lineHeight: 1.5, fontFamily: 'var(--orbis-font)' }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <PillButton label="Enter Orbis" onClick={finish} />
          </>
        )}

      </div>

      <style>{`
        @keyframes featurePop {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function PillButton({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 0,
        borderRadius: 100, border: '0.5px solid rgba(255,255,255,0.14)',
        background: 'rgba(255,255,255,0.05)', cursor: 'pointer', padding: 0,
        height: 54, overflow: 'hidden',
        transition: 'background 0.2s, border-color 0.2s, transform 0.12s',
        fontFamily: 'var(--orbis-font)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.26)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <span style={{ width: 54, height: 54, borderRadius: '50%', background: 'var(--orbis-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <ArrowIcon />
      </span>
      <span style={{ padding: '0 24px 0 18px', fontSize: 15, fontWeight: 400, color: 'var(--orbis-warm)', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </button>
  );
}
